import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  Campaign,
  Assessment,
  Groups,
  Settings,
  ExitToApp,
  Person,
  Notifications,
  Help,
  DarkMode,
  LightMode
} from '@mui/icons-material';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { APP_CONFIG } from '../../utils/constants';

const drawerWidth = 280;

const MainLayout = () => {
  const theme = useTheme();
  const { mode, toggle } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Menu items com controle de acesso
  const menuItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard />, 
      path: '/dashboard', 
      roles: ['viewer', 'analyst', 'manager', 'admin', 'owner'],
      description: 'Visão geral do sistema'
    },
    { 
      text: 'Empresas', 
      icon: <Business />, 
      path: '/companies', 
      roles: ['manager', 'admin', 'owner'],
      description: 'Gestão de empresas clientes'
    },
    { 
      text: 'Campanhas', 
      icon: <Campaign />, 
      path: '/campaigns', 
      roles: ['viewer', 'analyst', 'manager', 'admin', 'owner'],
      description: 'Campanhas de pesquisa'
    },
    { 
      text: 'Relatórios', 
      icon: <Assessment />, 
      path: '/reports', 
      roles: ['viewer', 'analyst', 'manager', 'admin', 'owner'],
      description: 'Análises e relatórios'
    }
  ];

  // Filtrar itens por role
  const visibleMenuItems = menuItems.filter(item => hasRole(item.roles));

  // Menu de plataforma (super-admin "escondido") — só aparece para platform admins
  if (user?.is_platform_admin) {
    visibleMenuItems.push({
      text: 'Consultorias',
      icon: <Groups />,
      path: '/consultorias',
      description: 'Gestão de consultorias (Ergofy)'
    });
  }

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  // Verificar se item está ativo
  const isActiveItem = (path) => location.pathname.startsWith(path);

  // Obter cor do role
  const getRoleColor = (role) => {
    const colors = {
      owner: 'error',
      admin: 'warning',
      manager: 'info',
      analyst: 'success',
      viewer: 'default'
    };
    return colors[role] || 'default';
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo e Título */}
      <Box sx={{
        p: 2.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`
      }}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <img
              src="/Ergofy-logo-tag-azul - Copia.png"
              alt="Ergofy Logo"
              style={{
                height: '28px',
                width: 'auto',
                marginBottom: '8px',
                // No dark mode, renderiza a logo (azul) em branco para legibilidade
                filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {APP_CONFIG.SUBTITLE}
            </Typography>
          </Box>
        </motion.div>
      </Box>
      
      {/* Menu Items */}
      <List sx={{ flexGrow: 1, px: 2, py: 1 }}>
        <AnimatePresence>
          {visibleMenuItems.map((item, index) => (
            <motion.div
              key={item.text}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isActiveItem(item.path)}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateX(4px)',
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main
                      }
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main
                      },
                      '& .MuiListItemText-primary': {
                        fontWeight: 600,
                        color: theme.palette.primary.main
                      }
                    }
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 40,
                      transition: 'color 0.2s'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    secondary={!isMobile ? item.description : null}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      sx: { mt: 0.5 }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </motion.div>
          ))}
        </AnimatePresence>
      </List>

      {/* User Info no Drawer */}
      <Box sx={{
        p: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
        background: alpha(theme.palette.text.primary, 0.03)
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
            {user?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={user?.role || 'user'}
                size="small"
                color={getRoleColor(user?.role)}
                sx={{ 
                  height: 20, 
                  fontSize: '0.6875rem',
                  fontWeight: 500
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div" fontWeight={600}>
              {/* Título dinâmico baseado na rota */}
              {visibleMenuItems.find(item => isActiveItem(item.path))?.text || 'Dashboard'}
            </Typography>
            {/* Nome da organização */}
            {user?.organization_name && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: '0.75rem' }}>
                {user.organization_name}
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Alternar tema claro/escuro */}
            <IconButton color="inherit" size="small" onClick={toggle}
              title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}>
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>

            {/* Notifications */}
            <IconButton color="inherit" size="small">
              <Notifications />
            </IconButton>

            {/* Help */}
            <IconButton color="inherit" size="small">
              <Help />
            </IconButton>

            {/* User Menu */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              ml: 1
            }}>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
                  {user?.name}
                </Typography>
                <Typography variant="caption" color="inherit" sx={{ opacity: 0.6 }}>
                  {user?.organization_name}
                </Typography>
              </Box>
              
              <IconButton onClick={handleMenuOpen} size="small">
                <Avatar sx={{ 
                  width: 36, 
                  height: 36,
                  bgcolor: alpha('#FFFFFF', 0.2),
                  color: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  {user?.name?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                }
              }}
            >
              <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
                <Person sx={{ mr: 2 }} />
                Meu Perfil
              </MenuItem>
              
              <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }}>
                <Settings sx={{ mr: 2 }} />
                Configurações
              </MenuItem>
              
              <Divider />
              
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ExitToApp sx={{ mr: 2 }} />
                Sair
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default'
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Outlet />
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default MainLayout;
